/**
 * API client for settlement operations.
 * Wraps GuestGuru backend endpoints for contributors and settlements.
 */

import { planningPath, apiGet, apiPost, apiPut, apiDelete } from './PlanningApiClient';
import { STORAGE_KEYS } from '../constants/storageKeys';
import {
  Contributor,
  Settlement,
  ContributionItem,
  PaymentProof,
} from '../types/ui/Settlement';

// ── Contributors ────────────────────────────────────────────────────

export async function fetchContributors(): Promise<Contributor[]> {
  const res = await apiGet<{ contributors: Contributor[] }>(planningPath('/contributors'));
  return Array.isArray(res) ? res : (res.contributors ?? []);
}

export async function fetchContributor(id: string): Promise<Contributor> {
  return apiGet<Contributor>(planningPath(`/contributors/${id}`));
}

export async function createContributor(
  data: Omit<Contributor, 'id' | 'created_at' | 'updated_at'>
): Promise<Contributor> {
  return apiPost<Contributor>(planningPath('/contributors'), data);
}

export async function updateContributor(
  id: string,
  updates: Partial<Contributor>
): Promise<Contributor> {
  return apiPut<Contributor>(planningPath(`/contributors/${id}`), updates);
}

export async function deleteContributor(id: string): Promise<void> {
  return apiDelete(planningPath(`/contributors/${id}`));
}

// ── Contribution Items ──────────────────────────────────────────────

export async function addContributionItem(
  contributorId: string,
  item: Omit<ContributionItem, 'id'>
): Promise<ContributionItem> {
  return apiPost<ContributionItem>(
    planningPath(`/contributors/${contributorId}/items`),
    item
  );
}

export async function removeContributionItem(
  contributorId: string,
  itemId: string
): Promise<void> {
  return apiDelete(planningPath(`/contributors/${contributorId}/items/${itemId}`));
}

// ── Settlements ─────────────────────────────────────────────────────

export async function fetchSettlements(): Promise<Settlement[]> {
  const res = await apiGet<{ settlements: Settlement[] }>(planningPath('/settlements'));
  return Array.isArray(res) ? res : (res.settlements ?? []);
}

export async function fetchSettlement(id: string): Promise<Settlement> {
  return apiGet<Settlement>(planningPath(`/settlements/${id}`));
}

export async function createSettlement(
  data: Omit<Settlement, 'id' | 'created_at'>
): Promise<Settlement> {
  return apiPost<Settlement>(planningPath('/settlements'), data);
}

export async function updateSettlement(
  id: string,
  updates: Partial<Settlement>
): Promise<Settlement> {
  return apiPut<Settlement>(planningPath(`/settlements/${id}`), updates);
}

export async function deleteSettlement(id: string): Promise<void> {
  return apiDelete(planningPath(`/settlements/${id}`));
}

// ── Settlement Payments ─────────────────────────────────────────────

export async function markSettlementCompleted(
  id: string,
  method: string,
  reference?: string
): Promise<Settlement> {
  return apiPut<Settlement>(planningPath(`/settlements/${id}`), {
    status: 'completed',
    method,
    reference,
    completed_at: new Date().toISOString(),
  });
}
